import { useCallback, useEffect, useState } from 'react'
import type { Api } from '../api'
import { ApiError, type ApiFailure, type AdminReport, type NewsItem } from '../api/types'
import { Button, EmptyState, Field, inputClass, Loading, Notice, ReportPicker } from '../components/ui'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FailureState } from '../components/FailureState'
import { SlideOver } from '../components/SlideOver'
import { REPORT_PAGES, type PageId } from '../components/Shell'

/**
 * NEWS — the landing page: what changed in the reports you can open.
 *
 * ---------------------------------------------------------------------------
 * THE ONE PAGE EVERYBODY CAN REACH
 *
 * This property predates the feed and survives it. A person granted no report
 * and holding no platform role would otherwise sign in to an empty frame with
 * an empty rail and nothing to read. So the PAGE is never gated — only the
 * cards on it, and the admin controls. An empty feed renders an EmptyState
 * saying so; it must never render a blank frame, which is the failure this
 * page was created to fix.
 *
 * ---------------------------------------------------------------------------
 * THE FEED ARRIVES ALREADY FILTERED
 *
 * plat.usp_List_Report_News_MBI joins through plat.fn_Openable_Reports_MBI, so a card
 * about a report this caller may not open never becomes a row. This component
 * does NOT filter, and must not start: a headline is a disclosure in its own
 * right — "Sales: cost and margin are now shown to holders of Sales_Cost" tells
 * a reader that a Sales report exists and what gates it. Deciding that here
 * would mean the sentence had already been sent to the browser.
 *
 * The admin controls are hidden on `roles`, and that is a COURTESY. Every write
 * is refused independently by the API and again by the procedure, which resolves
 * the actor from the object id rather than from anything this page sends.
 */
export function NewsPage({
  api,
  roles,
  viewAs,
  onNavigate,
}: {
  api: Api
  /**
   * The BORROWED roles under View As, and the caller's own otherwise — App
   * passes `surface`, not `me`. That is what makes the authoring controls
   * disappear when an administrator looks through somebody who is not one.
   */
  roles: string[]
  /**
   * Whose eyes this is being read through. View As means the caller BECOMES
   * the target, so the feed narrows with the sidebar rather than staying the
   * actor's own — a News page that ignored this would make View As lie about
   * the one screen everybody lands on.
   */
  viewAs: string | null
  /** Takes the reader to the report a card is about. */
  onNavigate: (page: PageId) => void
}) {
  const isSystemAdmin = roles.includes('SystemAdmin')

  const [items, setItems] = useState<NewsItem[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showPast, setShowPast] = useState(false)

  const [editing, setEditing] = useState<NewsItem | 'new' | null>(null)
  const [confirm, setConfirm] = useState<NewsItem | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setFailure(null)
    setItems(null)
    api
      .news(showPast)
      .then(setItems)
      .catch((e) =>
        setFailure(
          e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not read the updates.' },
        ),
      )
  }, [api, showPast, viewAs])

  useEffect(load, [load])

  // A forbidden or no-permissions failure replaces the page. Anything else —
  // including a failed publish — keeps it, so nobody loses what they typed.
  if (failure && (failure.kind === 'forbidden' || failure.kind === 'no-permissions')) {
    return <FailureState failure={failure} />
  }

  async function remove(item: NewsItem) {
    setBusy(true)
    setActionError(null)
    try {
      await api.retireNews(item.newsId)
      setConfirm(null)
      load()
    } catch (e) {
      setActionError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    // Full width, like every other page in the platform. An earlier version
    // capped this to a reading measure; it left the right third of the frame
    // empty and read as a page that had failed to load rather than as a
    // deliberate column.
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow">What's new</div>
          {/* No standing subtitle. The cards say what changed and who it is
              for; a line above them restating that is a caption on a page
              that does not need one, and it was the first thing a reader had
              to skip past to reach the actual news. */}
          <h1 className="mt-1 text-(length:--text-xl) font-semibold tracking-[-0.015em] text-(--color-ink)">
            News
          </h1>
        </div>

        {isSystemAdmin && (
          <div className="flex shrink-0 items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-(length:--text-small) text-(--color-ink-3)">
              <input
                type="checkbox"
                className="accent-(--color-navy-700)"
                checked={showPast}
                onChange={(e) => setShowPast(e.target.checked)}
              />
              Show past updates
            </label>
            <Button variant="primary" onClick={() => setEditing('new')}>
              New update
            </Button>
          </div>
        )}
      </header>

      {actionError && (
        <Notice tone="bad" title="That did not work." onDismiss={() => setActionError(null)}>
          {actionError}
        </Notice>
      )}

      {failure ? (
        // Anything that is not a refusal keeps the page and offers a retry — the
        // feed failing to load is not a reason to lose the header, or the
        // authoring controls an admin came here to use.
        <FailureState failure={failure} onRetry={load} />
      ) : items === null ? (
        <Loading label="Reading the latest updates…" />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nothing to report."
          detail={
            isSystemAdmin
              ? 'No updates have been published yet. Use “New update” to write the first one.'
              : 'When something changes in a report you can open, it will appear here.'
          }
        />
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <NewsCard
              key={item.newsId}
              item={item}
              index={i}
              open={expanded === item.newsId}
              isSystemAdmin={isSystemAdmin}
              onToggle={() => setExpanded(expanded === item.newsId ? null : item.newsId)}
              onOpenReport={() => {
                const page = PAGE_FOR_SLUG[item.reportSlug]
                if (page) onNavigate(page)
              }}
              onEdit={() => setEditing(item)}
              onRemove={() => setConfirm(item)}
            />
          ))}
        </div>
      )}

      {editing && (
        <NewsComposer
          api={api}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title="Remove this update?"
          detail={
            <>
              <strong className="font-semibold text-(--color-ink)">{confirm.headline}</strong> will
              stop appearing for everyone who can see it. The update is kept, and a System Admin can
              still find it under “Show past updates”.
            </>
          }
          confirmLabel="Remove update"
          busy={busy}
          onConfirm={() => void remove(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------------
   One card.

   THE NAVY RULE DOWN THE LEFT EDGE is the platform's existing mark for "this is
   a statement, read it" — FailureState uses the same 3px rule and says why: it
   reads as a margin mark on a document rather than a browser notification. An
   announcement about a report belongs in that register, and it is what ties the
   card back to the navy rail rather than leaving a white box floating.

   A past card keeps the anatomy and loses the colour: the rule goes grey. Same
   object, quieter — the same relationship StatTile's dense variant has to its
   full one.

   Expanding happens in place rather than opening a detail view: this app has no
   router — navigation is in-memory PageId state — so a detail route would mean
   a new PageId, a new branch in App's dispatch and a new case in canReach, for
   a paragraph of text.
--------------------------------------------------------------------------- */
function NewsCard({
  item,
  index,
  open,
  isSystemAdmin,
  onToggle,
  onOpenReport,
  onEdit,
  onRemove,
}: {
  item: NewsItem
  index: number
  open: boolean
  isSystemAdmin: boolean
  onToggle: () => void
  onOpenReport: () => void
  onEdit: () => void
  onRemove: () => void
}) {
  const fresh = daysSince(item.publishedUtc) <= 7 && !item.isExpired && !item.isRetired
  const past = item.isExpired || item.isRetired

  /* Whether this card has anywhere to send the reader. The composer now only
     offers reports that do, but a card published before that filter existed —
     or written straight against the API — can still name one this client has no
     page for. The button is hidden rather than disabled: there is nothing the
     reader could do to make it work, so offering it greyed out would only pose a
     question with no answer. */
  const opensTo = PAGE_FOR_SLUG[item.reportSlug]

  return (
    <article
      className={`anim-rise group overflow-hidden rounded-md border border-l-[3px] border-(--color-line) bg-(--color-surface) shadow-(--shadow-card) transition-shadow hover:shadow-(--shadow-raised) ${
        past ? 'border-l-(--color-line-strong)' : 'border-l-(--color-navy-700)'
      }`}
      // Staggered only slightly, and only on transform — a feed that fades in
      // one card at a time makes the reader wait to find out whether there is
      // anything to read.
      style={{ animationDelay: `${Math.min(index, 6) * 45}ms` }}
    >
      <div className="px-5 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {/* The report is the classifier, so it carries the brand colour.
                Grey micro-text here read as a caption on the headline rather
                than as the thing that says who this is for. */}
            <span
              className={`eyebrow ${past ? 'text-(--color-ink-4)' : 'text-(--color-navy-700)'}`}
            >
              {item.reportDisplayName}
            </span>
            {fresh && <NewChip />}
            {item.isRetired && <PastChip>Removed</PastChip>}
            {item.isExpired && !item.isRetired && <PastChip>Expired</PastChip>}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <time
              dateTime={item.publishedUtc}
              className="num text-(length:--text-small) text-(--color-ink-3)"
            >
              {postedOn(item.publishedUtc)}
            </time>
            {isSystemAdmin && !past && (
              // Held back until the card is hovered or something inside it takes
              // focus. Edit and Remove are the author's tools, not the reader's,
              // and on the page everyone lands on the reader is almost always
              // who is looking.
              //
              // pointer-events-none WHILE INVISIBLE, and it is not decoration.
              // `opacity: 0` hides a control from the eye and changes nothing
              // about hit-testing, so without this the Remove button is a live
              // target sitting under the reader's finger. On a touchscreen there
              // is no hover to reveal it first, and Remove is destructive: a tap
              // landing here retires the card its author just published, with no
              // confirmation and nothing on screen to explain what was pressed.
              // Restoring pointer events alongside the opacity keeps the two in
              // step -- the control becomes clickable exactly when it becomes
              // visible, on hover, on focus, and on the tap that emulates hover.
              <div className="ml-1 flex items-center gap-0.5 opacity-0 pointer-events-none transition-opacity focus-within:opacity-100 focus-within:pointer-events-auto group-hover:opacity-100 group-hover:pointer-events-auto">
                <button
                  type="button"
                  onClick={onEdit}
                  className="rounded px-2 py-1 text-(length:--text-small) font-medium text-(--color-accent) transition-colors hover:bg-(--color-navy-50)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  aria-label={`Remove update: ${item.headline}`}
                  onClick={onRemove}
                  className="rounded p-1.5 leading-none text-(--color-ink-4) transition-colors hover:bg-(--color-overdue)/8 hover:text-(--color-overdue)"
                >
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                    <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        <h2
          className={`mt-1.5 text-(length:--text-lg) font-semibold tracking-[-0.01em] ${
            past ? 'text-(--color-ink-2)' : 'text-(--color-ink)'
          }`}
        >
          {item.headline}
        </h2>

        <p className="mt-1.5 text-(length:--text-base) leading-relaxed text-(--color-ink-2)">
          {item.summary}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-1">
          {item.details && (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              className="flex items-center gap-1.5 rounded px-2 py-1 text-(length:--text-small) font-medium text-(--color-ink-2) transition-colors hover:bg-(--color-surface-3)"
            >
              <svg
                width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="2" aria-hidden="true"
                className={`transition-transform ${open ? 'rotate-90' : ''}`}
              >
                <path d="M6 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {open ? 'Hide details' : 'Details'}
            </button>
          )}
          {opensTo && (
            <button
              type="button"
              onClick={onOpenReport}
              className="rounded px-2 py-1 text-(length:--text-small) font-medium text-(--color-accent) transition-colors hover:bg-(--color-navy-50)"
            >
              Open {item.reportDisplayName} →
            </button>
          )}
        </div>
      </div>

      {open && item.details && (
        // Set on the recessed surface and separated by a hairline, so the detail
        // reads as the back of the card rather than as a second card.
        <div className="border-t border-(--color-line) bg-(--color-surface-2) px-5 py-4">
          {/* PLAIN TEXT, deliberately. Rendering this as HTML would make the one
              page every user lands on a stored-XSS surface. */}
          <p className="whitespace-pre-wrap text-(length:--text-base) leading-relaxed text-(--color-ink-2)">
            {item.details}
          </p>
        </div>
      )}
    </article>
  )
}

/**
 * "New" — navy, not green.
 *
 * Badge tone="good" would have been the easy reach and is wrong: in this
 * platform --color-good is a semantic, and it means money behaving well. A
 * recency marker borrowing it teaches the reader that green means two different
 * things on two different screens. Recency is not a health signal, so it wears
 * the brand instead.
 */
function NewChip() {
  return (
    <span className="inline-flex items-center rounded-full border border-(--color-navy-700)/20 bg-(--color-navy-50) px-2 py-0.5 text-(length:--text-micro) font-semibold tracking-[0.04em] text-(--color-navy-700) uppercase">
      New
    </span>
  )
}

/** Expired or removed. Neutral by design — it is a fact, not a warning. */
function PastChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-(--color-line-strong) bg-(--color-surface-3) px-2 py-0.5 text-(length:--text-micro) font-semibold tracking-[0.04em] text-(--color-ink-3) uppercase">
      {children}
    </span>
  )
}

/* ---------------------------------------------------------------------------
   Compose / edit.

   One panel for both. An edit seeds it from the card and keeps the same dates —
   the procedure leaves Published_Utc and Expires_Utc alone, so a corrected typo
   neither jumps back to the top of the feed nor restarts its month.
--------------------------------------------------------------------------- */
function NewsComposer({
  api,
  item,
  onClose,
  onSaved,
}: {
  api: Api
  /** null to write a new update; an item to correct it. */
  item: NewsItem | null
  onClose: () => void
  onSaved: () => void
}) {
  const [reports, setReports] = useState<AdminReport[] | null>(null)
  const [slug, setSlug] = useState(item?.reportSlug ?? '')
  const [headline, setHeadline] = useState(item?.headline ?? '')
  const [summary, setSummary] = useState(item?.summary ?? '')
  const [details, setDetails] = useState(item?.details ?? '')
  const [days, setDays] = useState(30)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .adminReports()
      /* ONLY REPORTS A CARD CAN ACTUALLY POINT AT.
         --------------------------------------------------------------------
         plat.Report_MBI holds every GOVERNABLE object, not every report. The Access
         screen is one of them — seeded as ('permissions', N'User Permissions')
         in 001_platform_schema.sql — because who may open it is granted and
         revoked exactly like a report. plat.usp_List_Admin_Reports_MBI therefore
         filters on nothing but Is_Retired, which is right for the screens that
         administer all of it and wrong for a composer.

         PermissionsPage already draws this line for its scoped tabs; this is the
         same line, drawn for the same reason. Until it was drawn here too, the
         composer offered "User Permissions" as somewhere to publish, and a card
         published against it would have been real and half-broken: every bypass
         holder would see it, headed by a chip naming a screen they do not think
         of as a report, above an Open button that does nothing at all.

         The test is PAGE_FOR_SLUG rather than Scoping_Model because it is the
         condition that actually has to hold — a card's one action is to open its
         report, so a report with no page cannot carry one. It is also the
         stricter of the two: a future data-scoped report becomes publishable the
         moment it gets a page and not before, which is the safe order to find
         the gap in.

         The card being edited is kept whatever it points at, so correcting a
         typo on an older card can never silently move it to another report. */
      .then((r) => {
        if (cancelled) return
        setReports(r.filter((x) => PAGE_FOR_SLUG[x.slug] !== undefined || x.slug === item?.reportSlug))
      })
      /* A failed list must SAY so. An empty list alone disables the picker behind
         a plain "Choose a report", and a new card can then never be published,
         with nothing on screen saying why. Routed to the composer's one existing
         Notice, not to a new error surface. An edit keeps its seeded report and
         can still be saved, so it is told only that the report is fixed. */
      .catch((e) => {
        if (cancelled) return
        setReports([])
        setError(
          item?.reportSlug !== undefined
            ? `The list of reports could not be loaded, so the report this update points at cannot be changed. ${messageOf(e)}`
            : `The list of reports could not be loaded, so there is nothing to publish against. ${messageOf(e)}`,
        )
      })
    return () => {
      cancelled = true
    }
  }, [api, item?.reportSlug])

  const ready = slug !== '' && headline.trim() !== '' && summary.trim() !== ''

  async function save() {
    setBusy(true)
    setError(null)
    try {
      if (item) {
        await api.updateNews(item.newsId, { reportSlug: slug, headline, summary, details })
      } else {
        await api.publishNews({ reportSlug: slug, headline, summary, details, durationDays: days })
      }
      onSaved()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SlideOver
      title={item ? 'Edit update' : 'New update'}
      description={
        item
          ? 'A correction. The date it was published and how long it runs stay as they were.'
          : 'Say what changed for the people who use the report, not how it was built. Only people who can open the report will see it.'
      }
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={!ready || busy}>
            {busy ? 'Working…' : item ? 'Save changes' : 'Publish'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <Notice tone="bad" title="That did not work." onDismiss={() => setError(null)}>
            {error}
          </Notice>
        )}

        <ReportPicker reports={reports} value={slug} onChange={setSlug} busy={busy} />

        <Field label="Headline" hint="One line. It is what people read first.">
          <input
            className={inputClass}
            value={headline}
            maxLength={120}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Trends now open in about a second"
          />
        </Field>

        <Field label="Summary" hint="A sentence or two on what is different for them.">
          <textarea
            className={`${inputClass} min-h-20 resize-y`}
            value={summary}
            maxLength={400}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="The weekly trend used to take around six seconds to draw. It now appears in about one."
          />
        </Field>

        <Field
          label="Details (optional)"
          hint="Shown when somebody expands the card. Plain text — line breaks are kept."
        >
          <textarea
            className={`${inputClass} min-h-40 resize-y`}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
          />
        </Field>

        {!item && (
          <Field label="Show for" hint="Days. Between 1 and 365; 30 is the usual choice.">
            <input
              type="number"
              className={inputClass}
              value={days}
              min={1}
              max={365}
              onChange={(e) => setDays(Number(e.target.value))}
            />
          </Field>
        )}
      </div>
    </SlideOver>
  )
}

/* ------------------------------------------------------------------ helpers */

/**
 * Which page a report's slug leads to.
 *
 * Here rather than on the server: the slug is the platform's identifier for a
 * report, and PageId is this client's own navigation vocabulary. Sending the
 * second from the API would make the backend responsible for the shape of the
 * sidebar.
 *
 * DERIVED FROM NAV, not written out. This map is what the composer's filter
 * tests against — a report absent from it cannot be published about, which is
 * the correct rule and was applied to the wrong list. Hand-maintained, it
 * still held two reports the day after a third shipped, so Material Planning
 * was fetched from the API and then silently filtered out of its own picker.
 * The safety property the filter was written for is unchanged: a report
 * becomes publishable the moment it has a page, and not before. It just now
 * reads that from the list that decides it.
 */
const PAGE_FOR_SLUG: Record<string, PageId | undefined> =
  Object.fromEntries(REPORT_PAGES.map((r) => [r.slug, r.page]))

/**
 * "4 Aug 2026". en-GB and UTC, so everybody reads the same date.
 *
 * NOT relativeTime() from ui.tsx. That one measures future-minus-now for
 * maintenance countdowns, so every past timestamp comes back as "now" — it
 * would render every card as posted this instant.
 */
function postedOn(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Whole days since publication; used only to decide the "New" chip. */
function daysSince(iso: string): number {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return Number.MAX_SAFE_INTEGER
  return Math.floor((Date.now() - t) / 86_400_000)
}

/**
 * The database's own guard text reaches the user verbatim when there is one.
 * Those messages are written for an administrator — "a headline and a summary
 * are required" — and a generic "something went wrong" would leave somebody
 * pressing a button that will never work.
 */
function messageOf(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Something went wrong. Please try again.'
}
